import React, { useEffect, useState, useMemo } from "react";
import { Search, CheckCheck, XCircle, Plus, Pencil, X } from "lucide-react";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const STATUS_STYLE = {
  PENDING:  "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  APPROVED: "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  REJECTED: "text-red-400 bg-red-500/10 border-red-500/20",
  PAID:     "text-purple-400 bg-purple-500/10 border-purple-500/20",
};

export default function ReimbursementsTab({ month }) {
  const [claims,   setClaims]   = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [statusF,  setStatusF]  = useState("PENDING");
  const [selected, setSelected] = useState(new Set());
  const [comments, setComments] = useState({});
  const [editing,  setEditing]  = useState(null);
  const [draft,    setDraft]    = useState({});
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState(null);
  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        fetch(`${API}/api/admin/payroll/reimbursements?month=${month}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API}/api/admin/payroll/reimbursement-policies`,        { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);
      setClaims(Array.isArray(c) ? c : c?.items || []);
      setPolicies(Array.isArray(p) ? p : []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [month]);

  const visible = useMemo(() => {
    let out = claims;
    if (statusF !== "ALL") out = out.filter(c => c.status === statusF);
    if (search)  out = out.filter(c => (c.employee_name || "").toLowerCase().includes(search.toLowerCase()) || (c.category || "").toLowerCase().includes(search.toLowerCase()));
    return out;
  }, [claims, statusF, search]);

  const act = async (id, action, note = "") => {
    try {
      await fetch(`${API}/api/admin/payroll/reimbursements/${id}/${action}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      });
      const status = { approve: "APPROVED", reject: "REJECTED" }[action];
      setClaims(prev => prev.map(c => c.id === id ? { ...c, status } : c));
    } catch (e) { setMsg({ type: "error", text: e.message }); }
  };

  const bulkAct = async (action) => {
    try {
      await fetch(`${API}/api/admin/payroll/reimbursements/bulk`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action, ids: [...selected] }),
      });
      const status = action === "approve" ? "APPROVED" : "REJECTED";
      setClaims(prev => prev.map(c => selected.has(c.id) ? { ...c, status } : c));
      setSelected(new Set());
    } catch (e) { setMsg({ type: "error", text: e.message }); }
  };

  const savePolicy = async () => {
    setSaving(true);
    try {
      const isNew = editing === "new";
      await fetch(isNew ? `${API}/api/admin/payroll/reimbursement-policies` : `${API}/api/admin/payroll/reimbursement-policies/${draft.id}`, {
        method: isNew ? "POST" : "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      await load(); setEditing(null);
    } catch (e) { setMsg({ type: "error", text: e.message }); }
    finally { setSaving(false); }
  };

  const delPolicy = async (id) => {
    await fetch(`${API}/api/admin/payroll/reimbursement-policies/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    setPolicies(p => p.filter(x => x.id !== id));
  };

  const someSelected = selected.size > 0;
  const setComment = (id, v) => setComments(c => ({ ...c, [id]: v }));
  const toggle = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="space-y-5">

      {/* Reimbursement Policy */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Reimbursement Policies</span>
          <button onClick={() => { setDraft({ category: "", grade: "ALL", monthly_cap: 0, annual_cap: 0 }); setEditing("new"); }}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
            <Plus size={12} /> Add Policy
          </button>
        </div>

        {editing && (
          <div className="px-5 py-4 border-b border-white/5 bg-[#00ff90]/3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Category</label>
                <input className={inputCls} value={draft.category || ""} onChange={e => setDraft(d => ({...d, category: e.target.value}))} placeholder="Fuel / Medical / Travel…" /></div>
              <div><label className={labelCls}>Grade / Scope</label>
                <input className={inputCls} value={draft.grade || "ALL"} onChange={e => setDraft(d => ({...d, grade: e.target.value}))} placeholder="ALL / L4 / L5…" /></div>
              <div><label className={labelCls}>Monthly Cap (₹)</label>
                <input type="number" className={inputCls} value={draft.monthly_cap || ""} onChange={e => setDraft(d => ({...d, monthly_cap: parseFloat(e.target.value)}))} /></div>
              <div><label className={labelCls}>Annual Cap (₹)</label>
                <input type="number" className={inputCls} value={draft.annual_cap || ""} onChange={e => setDraft(d => ({...d, annual_cap: parseFloat(e.target.value)}))} /></div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
              <button onClick={savePolicy} disabled={saving}
                className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}

        <div className="divide-y divide-white/5 max-h-48 overflow-y-auto">
          {policies.length === 0 ? <p className="px-5 py-4 text-white/20 text-sm italic text-center">No policies configured.</p>
          : policies.map(p => (
            <div key={p.id} className="flex items-center justify-between gap-3 px-5 py-3">
              <div>
                <p className="text-sm text-white/70 font-medium">{p.category}</p>
                <p className="text-xs text-white/25">{p.grade} · Monthly: ₹{Number(p.monthly_cap).toLocaleString()} · Annual: ₹{Number(p.annual_cap).toLocaleString()}</p>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => { setDraft({...p}); setEditing(p.id); }} className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors"><Pencil size={12} /></button>
                <button onClick={() => delPolicy(p.id)} className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors"><X size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Claims */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3 flex-wrap">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Claims — {month}</span>
          <div className="flex items-center gap-2 ml-auto">
            <Search size={13} className="text-white/30" />
            <input className={inputCls} placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 160 }} />
            <select className={inputCls} value={statusF} onChange={e => setStatusF(e.target.value)}>
              {["ALL","PENDING","APPROVED","REJECTED","PAID"].map(s => <option key={s} value={s}>{s === "ALL" ? "All Statuses" : s}</option>)}
            </select>
          </div>
        </div>

        {someSelected && (
          <div className="px-5 py-3 border-b border-white/5 flex items-center gap-3 bg-[#00ff90]/3">
            <span className="text-xs text-[#00ff90]/70">{selected.size} selected</span>
            <button onClick={() => bulkAct("approve")} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors"><CheckCheck size={12} /> Bulk Approve</button>
            <button onClick={() => bulkAct("reject")}  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-red-500/25 bg-red-500/8 text-red-400/70 hover:bg-red-500/15 hover:text-red-400 transition-colors"><XCircle size={12} /> Bulk Reject</button>
            <button onClick={() => setSelected(new Set())} className="text-xs text-white/30 hover:text-white ml-auto transition-colors">Clear</button>
          </div>
        )}

        {msg && <p className={`px-5 py-3 text-xs ${msg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{msg.text}</p>}

        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0d0d0d]">
              <tr className="border-b border-white/5">
                <th className="px-4 py-3 w-8"><input type="checkbox" onChange={e => setSelected(e.target.checked ? new Set(visible.map(c => c.id)) : new Set())} className="w-4 h-4 rounded accent-[#00ff90]" /></th>
                {["Employee","Category","Amount","Date","Status",""].map(h => <th key={h} className="px-3 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading ? Array.from({length:4}).map((_,i) => <tr key={i}><td colSpan={7} className="px-4 py-3"><div className="h-8 rounded bg-white/5 animate-pulse" /></td></tr>)
              : visible.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-white/20 italic text-sm">No claims found.</td></tr>
              : visible.map(c => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                  <td className="px-4 py-3"><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} className="w-4 h-4 rounded accent-[#00ff90]" /></td>
                  <td className="px-3 py-3"><p className="text-sm text-white/70">{c.employee_name}</p><p className="text-xs text-white/30">{c.employee_id}</p></td>
                  <td className="px-3 py-3 text-white/50 text-xs">{c.category}</td>
                  <td className="px-3 py-3 text-white/70 text-sm">₹{Number(c.amount).toLocaleString()}</td>
                  <td className="px-3 py-3 text-white/30 text-xs">{c.date}</td>
                  <td className="px-3 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[c.status] || STATUS_STYLE.PENDING}`}>{c.status}</span></td>
                  <td className="px-3 py-3">
                    {c.status === "PENDING" && (
                      <div className="flex gap-1.5">
                        <input placeholder="Note…" value={comments[c.id] || ""} onChange={e => setComment(c.id, e.target.value)} className="bg-black border border-white/10 rounded-lg px-2 py-1 text-xs text-white outline-none w-20" />
                        <button onClick={() => act(c.id, "approve", comments[c.id] || "")} className="text-xs px-2 py-1 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">✓</button>
                        <button onClick={() => act(c.id, "reject",  comments[c.id] || "")} className="text-xs px-2 py-1 rounded-lg border border-red-500/20 bg-red-500/5 text-red-400/60 hover:bg-red-500/15 hover:text-red-400 transition-colors">✗</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}